import type { PlanKey } from 'markcat-api-client';

export type BillingPeriod = 'monthly' | 'annual';
export type SubscriptionPlanKey = Exclude<PlanKey, `topup_${string}`>;

interface PlanPrice {
  key: SubscriptionPlanKey;
  cents: number;
  annualNote?: string;
}

export interface PricingPlan {
  id: 'basic' | 'pro';
  name: string;
  description: string;
  prices: Record<BillingPeriod, PlanPrice>;
  features: readonly string[];
  highlight?: boolean;
}

export interface PriceDisplay {
  key: SubscriptionPlanKey;
  amount: string;
  period: '/ month' | '/ year';
  annualNote: string | undefined;
  savingsPercent: number | null;
}

const BASIC: PricingPlan = {
  id: 'basic',
  name: 'Basic',
  description: 'For steady bookmark savers.',
  prices: {
    monthly: { key: 'basic_monthly', cents: 299 },
    annual: {
      key: 'basic_annual',
      cents: 2900,
      annualNote: '+2,000 classification credits',
    },
  },
  features: [
    '200 classifications / month',
    'Up to 10,000 bookmarks stored',
    'Full fetch pipeline',
    '1 summary language',
  ],
};

const PRO: PricingPlan = {
  id: 'pro',
  name: 'Pro',
  description: 'For power users and library migrations.',
  prices: {
    monthly: { key: 'pro_monthly', cents: 499 },
    annual: {
      key: 'pro_annual',
      cents: 5900,
      annualNote: '+10,000 classification credits — the migration plan',
    },
  },
  features: [
    '500 classifications / month',
    'Up to 50,000 bookmarks stored',
    'Full fetch pipeline',
    '3 summary languages',
    'Priority processing queue',
  ],
  highlight: true,
};

export const PRICING_CATALOG = {
  basic_monthly: BASIC,
  basic_annual: BASIC,
  pro_monthly: PRO,
  pro_annual: PRO,
} satisfies Record<SubscriptionPlanKey, PricingPlan>;

export const PAID_PLAN_ORDER = [
  'basic_monthly',
  'pro_monthly',
] as const satisfies readonly SubscriptionPlanKey[];

function formatEuros(cents: number): string {
  const euros = cents / 100;
  return `€${Number.isInteger(euros) ? euros : euros.toFixed(2)}`;
}

function annualSavingsPercent(plan: PricingPlan): number | null {
  const monthlyTotal = plan.prices.monthly.cents * 12;
  const percent = Math.round((1 - plan.prices.annual.cents / monthlyTotal) * 100);
  return percent >= 10 ? percent : null;
}

export function getPriceDisplay(plan: PricingPlan, period: BillingPeriod): PriceDisplay {
  const price = plan.prices[period];
  return {
    key: price.key,
    amount: formatEuros(price.cents),
    period: period === 'monthly' ? '/ month' : '/ year',
    annualNote: period === 'annual' ? price.annualNote : undefined,
    savingsPercent: period === 'annual' ? annualSavingsPercent(plan) : null,
  };
}
